import React from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Button,
  Grid,
  Chip,
  Divider,
  Alert,
  Container
} from '@mui/material';
import { 
  AccountCircle as AccountIcon,
  Business as BusinessIcon,
  Science as ScienceIcon,
  ExitToApp as LogoutIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const AuthInfo = () => {
  const { 
    user, 
    isAuthenticated, 
    logout,
    getUsername,
    getOrgId,
    getAppId,
    getLabTypeId,
    getUserTypeId,
    isSubUser,
    getMainOrgId,
    isLabUser
  } = useAuth();

  if (!isAuthenticated) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          You are not authenticated. Please log in to view your information.
        </Alert>
      </Container>
    );
  }

  const username = getUsername();
  const orgId = getOrgId();
  const appId = getAppId();
  const labTypeId = getLabTypeId();
  const userTypeId = getUserTypeId();
  const isSubUserAccount = isSubUser();
  const mainOrgId = getMainOrgId();

  // Get user type description
  const getUserTypeDescription = (userTypeId) => {
    switch (userTypeId) {
      case '1':
      case '2':
      case '24':
        return 'Administrator';
      case '3':
        return 'Regional Representative';
      case '4':
        return 'Designation Officer';
      case '5':
        return 'Accounts';
      case '6':
        return 'Assessor';
      case '8':
        return 'Director';
      case '9':
        return 'Joint Director';
      case '10':
        return 'CEO';
      case '11':
        return 'Convener';
      default:
        return 'Laboratory User';
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h5" component="h1" color="primary">
              Authentication Information
            </Typography>
          </Box>

          <Alert severity="success" sx={{ mb: 3 }}>
            You are successfully authenticated and logged in to the NABL Portal.
          </Alert>

          <Grid container spacing={3}>
            {/* User Information */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AccountIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">User Details</Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Username
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {username || 'N/A'}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      User Type
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" fontWeight="medium">
                        {getUserTypeDescription(userTypeId)}
                      </Typography>
                      <Chip 
                        label={userTypeId || 'Lab User'} 
                        size="small" 
                        color={isLabUser() ? 'primary' : 'secondary'}
                      />
                    </Box>
                  </Box>

                  {isSubUserAccount && (
                    <Box sx={{ mb: 2 }}>
                      <Chip 
                        label="Sub User Account" 
                        size="small" 
                        color="warning"
                        variant="outlined"
                      />
                      {mainOrgId && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Main Org ID: {mainOrgId}
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Organization Information */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Organization Details</Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Organization ID (orgId)
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {orgId || 'N/A'}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Application ID (appId)
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {appId || 'N/A'}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Laboratory Type ID (labTypeId)
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" fontWeight="medium">
                        {labTypeId || 'N/A'}
                      </Typography>
                      <ScienceIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Stored Data Information */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ backgroundColor: '#f5f5f5' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    LocalStorage Data
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    The following data is stored in your browser's localStorage:
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" fontWeight="medium">
                        authToken: ✓ Present
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" fontWeight="medium">
                        username: {username ? '✓' : '✗'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" fontWeight="medium">
                        orgId: {orgId ? '✓' : '✗'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" fontWeight="medium">
                        appId: {appId ? '✓' : '✗'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" fontWeight="medium">
                        labTypeId: {labTypeId ? '✓' : '✗'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" fontWeight="medium">
                        userData: ✓ Present
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Actions */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Login Time: {user?.loginTime ? new Date(user.loginTime).toLocaleString() : 'N/A'}
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<LogoutIcon />}
                  onClick={handleLogout}
                >
                  Logout
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AuthInfo;